import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BranchSelector } from "@/components/branch-selector";
import { PageContent } from "@/components/layout/page-content";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import { toast } from "@/lib/toast";

interface TaxRatesSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/settings/tax")({
	component: TaxRatesPage,
	validateSearch: (search: Record<string, unknown>): TaxRatesSearch => ({
		branch: search.branch as string | undefined,
	}),
});

const updateTaxSchema = z.object({
	taxId: z.string().optional(),
	taxRate: z.number().min(0).max(100).optional(),
});

type UpdateTaxInput = z.infer<typeof updateTaxSchema>;

function TaxRatesPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/settings/tax",
	});

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const branchData =
		branchesData === undefined
			? undefined
			: (branchesData.find((b) => b._id === branchId) ?? null);

	const updateBranch = useMutation(api.features.branches.web.update);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting, isDirty },
	} = useForm<UpdateTaxInput>({
		resolver: zodResolver(updateTaxSchema),
		defaultValues: { taxId: "", taxRate: undefined },
	});

	useEffect(() => {
		if (branchData) {
			reset({
				taxId: branchData.taxId ?? "",
				taxRate: branchData.taxRate,
			});
		}
	}, [branchData, reset]);

	const onSubmit = async (data: UpdateTaxInput) => {
		if (!orgId || !branchId) return;
		try {
			await updateBranch({
				orgId,
				branchId: branchId as Id<"branches">,
				taxId: data.taxId || undefined,
				taxRate: data.taxRate,
			});
			toast.success(<>Tax settings saved</>);
		} catch (error) {
			handleConvexError(error);
		}
	};

	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({ to: ".", search: { branch: value } });
		},
		[navigate],
	);

	const controls = (
		<BranchSelector
			branches={branches}
			value={branchId as Id<"branches"> | undefined}
			onValueChange={handleBranchChange}
			placeholder="Select branch"
			className="w-48"
			onViewAll={() => navigate({ to: "/settings/branches" })}
			onCreateNew={() => navigate({ to: "/settings/branches" })}
		/>
	);

	if (!branchId && !isLoadingBranches) {
		return (
			<PageContent title="Tax Rates" controls={controls}>
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Create a branch to configure its tax settings."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={null}
					/>
				</div>
			</PageContent>
		);
	}

	if (branchData === undefined) {
		return (
			<PageContent title="Tax Rates" controls={controls}>
				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</PageContent>
		);
	}

	return (
		<PageContent title="Tax Rates" controls={controls}>
			<div className="space-y-6">
				<div className="flex flex-col gap-2">
					<h2 className="text-2xl font-bold">Tax Rates</h2>
					<p className="text-muted-foreground">
						Configure tax identification and default tax rate for this branch.
					</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					<Card className="p-4 sm:p-6">
						<h3 className="text-lg font-semibold mb-4">Tax Configuration</h3>
						<FieldGroup>
							<div className="grid grid-cols-2 gap-4">
								<Field data-invalid={!!errors.taxId}>
									<FieldLabel htmlFor="taxId">Tax ID (optional)</FieldLabel>
									<FieldDescription>
										Tax identification number printed on receipts.
									</FieldDescription>
									<Input
										id="taxId"
										placeholder="e.g., 1234567890123"
										{...register("taxId")}
										aria-invalid={!!errors.taxId}
									/>
									<FieldError>{errors.taxId?.message}</FieldError>
								</Field>

								<Field data-invalid={!!errors.taxRate}>
									<FieldLabel htmlFor="taxRate">
										Tax rate % (optional)
									</FieldLabel>
									<FieldDescription>
										Default VAT/tax rate applied to sales at this branch.
									</FieldDescription>
									<Input
										id="taxRate"
										type="number"
										step="any"
										min={0}
										max={100}
										placeholder="e.g., 7"
										{...register("taxRate", {
											setValueAs: (v) => (v === "" ? undefined : Number(v)),
										})}
										aria-invalid={!!errors.taxRate}
									/>
									<FieldError>{errors.taxRate?.message}</FieldError>
								</Field>
							</div>
						</FieldGroup>
					</Card>

					<div className="flex justify-end">
						<Button type="submit" disabled={!isDirty || isSubmitting}>
							{isSubmitting ? "Saving..." : "Save changes"}
						</Button>
					</div>
				</form>
			</div>
		</PageContent>
	);
}
